import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Processing from 'ext-ui/components/Processing/Processing'
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js'
import React, { ComponentProps, useCallback, useEffect, useState } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectIsLookingUpFromState,
  selectLookupErrorDetailMapFromState,
  selectLookupTopLevelErrorFromState,
  selectVerificationEmailResentFromState,
  selectVerifyEmailTopLevelErrorFromState as selectCSPVerifyEmailTopLevelErrorFromState,
} from 'src/app/CspSignin/redux/cspSignin.selectors'
import {
  selectIsSigningInFromState,
  selectIsVerifyingUserFromState,
  selectSigninErrorDetailMapFromState,
  selectSigninTopLevelErrorFromState,
  selectVerificationPhoneLoadingFromState,
  selectVerifyEmailTopLevelErrorFromState,
} from 'src/app/Signin/redux/signin.selectors'
import SignInFormV2 from 'src/component-form/SignInFormV2/SignInFormV2'
import EditPhoneModal from 'src/component-modal/EditPhoneModal/EditPhoneModal'
import VerifyPhoneModal from 'src/component-modal/VerifyPhoneModal/VerifyPhoneModal'
import BrandPrimaryLogo from 'src/components/BrandPrimaryLogo/BrandPrimaryLogo'
import ExtraVerificationPopup from 'src/components/ExtraVerificationPopup/ExtraVerificationPopup'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SigninForm from 'src/components/SigninForm/SigninForm'
import UnauthedBoxContainer from 'src/components/UnauthedBoxContainer/UnauthedBoxContainer'
import UnauthedPageContainer from 'src/components/UnauthedPageContainer/UnauthedPageContainer'
import VerifyForm from 'src/components/VerifyForm/VerifyForm'
import { Environments } from 'src/configurations'
import {
  AuthTypes,
  CognitoErrorMessages,
  MfaDeliveryMedium,
  SupportedBrands,
  UserStatus,
} from 'src/enums'
import SigninCMS from 'src/feature/MarketingCMS/SigninCMS'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, {
  translateSingleString,
  // useTranslate,
} from 'src/localization/Trns'
import { Rdrct } from 'src/navigation/Lnk'
import { cspSigninUrl } from 'src/navigation/urls'
import {
  useSendPhoneCode,
  useVerifyPhoneCode,
} from 'src/queries/phoneVerification/domain/phoneVerification.queries'
import { createSetIsExtraVerificationPopupOpen } from 'src/redux/actions/app.actions'
import {
  confirmCognitoSignin,
  createAbandonMfaChallengeAction,
  createClearAuthFlowTypeAction,
  createSetEmail,
  createSetUserStatus,
  extendSignIn,
  getCognitoSigninThenPush,
  postAuthType,
  postSendPhoneVerificationCode,
  postUserResendVerification,
  postUserVerificationThenPostSignin,
} from 'src/redux/actions/auth.actions'
import { selectIsExtraVerificationPopupOpenFromState } from 'src/redux/selectors/app.selectors'
import {
  selectAuthTypeFromState,
  selectDidSessionExpireAutomaticallyFromState,
  selectEmailFromState,
  selectFetchAuthTypeTopLevelErrorFromState,
  selectIsCspAuthenticatedFromState,
  selectMfaChallengeInitiated,
  selectMfaDeliveryMediumFromState,
  selectMfaDestinationFromState,
  selectNullSafeLocaleFromState,
  selectPhoneNumberResponseValueFromState,
  selectResendVerificationTopLevelErrorFromState,
  selectUserEmailVerificationStatusFromState,
  selectUserStatusFromState,
  selectVerificationPhoneResentFromState,
  selectVerifyPhoneTopLevelErrorFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandName,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { isSSOLoginEnabled } from 'src/utils/featureFlags'
import { css } from 'styled-components'

import style, { boxStyling, halfPageStyling } from './SigninPage.style'

const PageAndBoxContainer: React.FC<
  {
    showVerifyForm: boolean
    brandName: SupportedBrands
    showVerifyPhoneForm: boolean
  } & ComponentProps<'div'>
> = ({ showVerifyForm, brandName, showVerifyPhoneForm, children }) => {
  return (
    <UnauthedPageContainer
      topLeftImage={
        showVerifyForm && brandName !== SupportedBrands.Bmo ? (
          <div
            css={css`
              position: relative;
              top: 30px;
              left: 30px;
            `}
          >
            <BrandPrimaryLogo />
          </div>
        ) : undefined
      }
    >
      <UnauthedBoxContainer
        className={'unauthedBoxContainer'}
        hideBrandLogo={showVerifyForm}
        hideFooter={showVerifyForm || showVerifyPhoneForm}
        noBox={showVerifyForm || showVerifyPhoneForm}
      >
        {children}
      </UnauthedBoxContainer>
    </UnauthedPageContainer>
  )
}

const SigninPage: React.FC = () => {
  // const translate = useTranslate()
  const email = useSelector(selectEmailFromState)
  const userStatus = useSelector(selectUserStatusFromState)
  const isLookingUp = useSelector(selectIsLookingUpFromState)
  const isSigningIn = useSelector(selectIsSigningInFromState)
  const isVerifyingUser = useSelector(selectIsVerifyingUserFromState)
  const verificationEmailResent = useSelector(
    selectVerificationEmailResentFromState,
  )
  const resendVerificationTopLevelError = useSelector(
    selectResendVerificationTopLevelErrorFromState,
  )

  const CSPVerifyEmailTopLevelError = useSelector(
    selectCSPVerifyEmailTopLevelErrorFromState,
  )
  const verifyEmailTopLevelError = useSelector(
    selectVerifyEmailTopLevelErrorFromState,
  )
  const verificationPhoneResent = useSelector(
    selectVerificationPhoneResentFromState,
  )
  const userEmailVerificationStatus = useSelector(
    selectUserEmailVerificationStatusFromState,
  )
  const verifyPhoneTopLevelError = useSelector(
    selectVerifyPhoneTopLevelErrorFromState,
  )
  const verifyPhoneLoading = useSelector(
    selectVerificationPhoneLoadingFromState,
  )
  const lookupTopLevelError = useSelector(selectLookupTopLevelErrorFromState)
  const lookupErrorDetailMap = useSelector(selectLookupErrorDetailMapFromState)
  const signinTopLevelError = useSelector(selectSigninTopLevelErrorFromState)
  const signinErrorDetailMap = useSelector(selectSigninErrorDetailMapFromState)
  const fetchAuthTypeTopLevelError = useSelector(
    selectFetchAuthTypeTopLevelErrorFromState,
  )
  const didSessionExpireAutomatically = useSelector(
    selectDidSessionExpireAutomaticallyFromState,
  )
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const env = useSelector(selectEnvironmentFromState)
  const mfaChallengeInitiated = useSelector(selectMfaChallengeInitiated)
  const authType = useSelector(selectAuthTypeFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const location = useSelector(selectLocationFromState)
  const brandName = useSelector(selectBrandName)
  const isExtraVerificationPopupOpen = useSelector(
    selectIsExtraVerificationPopupOpenFromState,
  )
  const mfaDestination = useSelector(selectMfaDestinationFromState)
  const mfaDeliveryMedium = useSelector(selectMfaDeliveryMediumFromState)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)

  const phoneNumberResponseValue = useSelector(
    selectPhoneNumberResponseValueFromState,
  )

  const { analytics } = useAnalyticsContext()
  const [userEmailVerified, setUserEmailVerified] = useState<boolean>(false)
  const dispatch = useBackgroundDispatch()

  useEffect((): void => {
    dispatch(createClearAuthFlowTypeAction())
  }, [])

  const dispatchSignin = (
    email: string,
    password?: string,
    newCode?: boolean,
  ): void => {
    dispatch(extendSignIn(email, password, newCode, analytics))
  }

  const dispatchSetEmail = (val: string): void => {
    dispatch(createSetEmail(val))
  }

  const dispatchSetUserStatus = (val?: UserStatus): void => {
    dispatch(createSetUserStatus(val))
  }

  const dispatchPostUserResendVerification = (val: string): void => {
    dispatch(postUserResendVerification(val))
  }

  const dispatchPostUserVerificationThenSignin = (code: string): void => {
    if (!authType) {
      dispatch(
        postAuthType({
          data: email,
        }),
      )
    }
    dispatch(
      postUserVerificationThenPostSignin(
        code,
        email,
        password,
        acceptOrgInvite,
      ),
    )
  }

  const dispatchConfirmCognitoSignin = (
    code: string,
    rememberDevice?: boolean,
  ): void => {
    dispatch(confirmCognitoSignin(code, rememberDevice))
  }

  const dispatchAbandonMfaChallenge = (): void => {
    dispatch(createAbandonMfaChallengeAction())
  }

  const [password, setPassword] = useState<string>(
    location.state?.password ?? '',
  )

  const acceptOrgInvite = !!location.state?.acceptOrgInvite

  const requirePhoneNumber = remoteConfig?.require_phone_number ?? false

  // New authenticated verify mutation (uses /users/phone/verifycode)
  const verifyPhoneCode = useVerifyPhoneCode()
  const [newFlowVerifyError, setNewFlowVerifyError] = useState<
    string | undefined
  >()

  // Edit phone state and mutation for login flow
  const sendPhoneCode = useSendPhoneCode()
  const [showEditPhone, setShowEditPhone] = useState(false)
  const [editPhoneError, setEditPhoneError] = useState<string | undefined>()
  const [phoneIsoCountry, setPhoneIsoCountry] = useState('US')
  const [phone, setPhone] = useState('')

  const handleEditPhone = useCallback((): void => {
    setEditPhoneError(undefined)
    setShowEditPhone(true)
  }, [])

  const handleEditPhoneSubmit = useCallback(
    async (newIsoCountry: string, newPhone: string): Promise<void> => {
      const newFullPhone = `+${getCountryCallingCode(newIsoCountry as CountryCode)}${newPhone}`
      try {
        await sendPhoneCode.mutateAsync({ phone: newFullPhone })
        setPhoneIsoCountry(newIsoCountry)
        setPhone(newPhone)
        setEditPhoneError(undefined)
        setNewFlowVerifyError(undefined)
        setShowEditPhone(false)
      } catch {
        setEditPhoneError('Failed to update phone number. Please try again.')
      }
    },
    [sendPhoneCode],
  )

  const handleEditPhoneGoBack = useCallback((): void => {
    setEditPhoneError(undefined)
    setShowEditPhone(false)
  }, [])

  const handleNewFlowPhoneVerify = useCallback(
    async (code: string): Promise<void> => {
      setNewFlowVerifyError(undefined)
      try {
        await verifyPhoneCode.mutateAsync({ code })
        dispatch(createSetIsExtraVerificationPopupOpen(false))
        await dispatch(getCognitoSigninThenPush(false, acceptOrgInvite))
      } catch {
        setNewFlowVerifyError(
          translateSingleString(
            locale,
            'Invalid verification code. Please try again.',
          ),
        )
      }
    },
    [dispatch, verifyPhoneCode, acceptOrgInvite],
  )

  const handleSigninPhoneResend = useCallback((): void => {
    dispatch(postSendPhoneVerificationCode(email, true))
  }, [dispatch, email])

  const maskedPhoneDisplay = phoneNumberResponseValue ?? ''

  const renderRequirePhoneVerification = (): React.ReactElement => (
    <>
      <VerifyPhoneModal
        isOpen={!showEditPhone}
        phoneNumber={maskedPhoneDisplay}
        isVerifying={verifyPhoneCode.isPending}
        verifyError={newFlowVerifyError}
        resendError={resendVerificationTopLevelError}
        verificationResent={verificationPhoneResent ?? false}
        onVerify={handleNewFlowPhoneVerify}
        onResend={handleSigninPhoneResend}
        onEditPhone={handleEditPhone}
      />
      <EditPhoneModal
        isOpen={showEditPhone}
        initialPhoneIsoCountry={phoneIsoCountry}
        initialPhone={phone}
        isSubmitting={sendPhoneCode.isPending}
        phoneError={editPhoneError}
        onSubmit={handleEditPhoneSubmit}
        onGoBack={handleEditPhoneGoBack}
        onClearError={() => setEditPhoneError(undefined)}
      />
    </>
  )

  const signinFormTopLevelError = (): string | undefined => {
    if (fetchAuthTypeTopLevelError) {
      return fetchAuthTypeTopLevelError
    }
    if (lookupTopLevelError || signinTopLevelError) {
      if (authType === AuthTypes.cognito) {
        switch (signinTopLevelError) {
          case CognitoErrorMessages.incorrectUsernameOrPassword:
            return translateSingleString(
              locale,
              'Unable to log in, please review your email/password',
            )
          case CognitoErrorMessages.passwordResetRequiredForTheUser:
            return translateSingleString(
              locale,
              "For security purposes, your password needs to be reset. Please click the 'Forgot Your Password?' link below to update your credentials.",
            )
          default:
            return (
              lookupTopLevelError ||
              (signinTopLevelError !== UserStatus.PhoneNotVerified
                ? signinTopLevelError
                : undefined)
            )
        }
      } else {
        return (
          lookupTopLevelError ||
          (signinTopLevelError !== UserStatus.PhoneNotVerified
            ? signinTopLevelError
            : undefined)
        )
      }
    } else {
      return undefined
    }
  }

  const appName = useSelector(selectAppName)
  const brand = useSelector(selectBrandName)
  const showSSOLogin = isSSOLoginEnabled(remoteConfig)

  const showVerifyForm =
    !isLookingUp &&
    !isSigningIn &&
    signinTopLevelError === UserStatus.NotVerified
  const showVerifyPhoneForm =
    !isLookingUp &&
    !isSigningIn &&
    signinTopLevelError === UserStatus.PhoneNotVerified

  useEffect((): void => {
    if (userEmailVerificationStatus === UserStatus.EmailVerified) {
      setUserEmailVerified(true)
    } else {
      setUserEmailVerified(false)
    }
  }, [userEmailVerificationStatus])

  useEffect((): void => {
    // Send initial email verification code when verification form appears
    // This is needed for both SSO and non-SSO flows
    if (showVerifyForm) {
      dispatchPostUserResendVerification(email)
    }
  }, [showVerifyForm, email, dispatchPostUserResendVerification])

  useEffect((): void => {
    // Open phone verification popup when needed
    // Note: Only send code if NOT coming from email verification (userEmailVerified = false)
    // to avoid duplicates since ExtraVerificationPopup already sends it after email verification
    if (showVerifyPhoneForm && !userEmailVerified) {
      // For non-SSO flow or direct phone verification scenarios
      // SSO flow handles this via ExtraVerificationPopup after email verification
      if (!showSSOLogin) {
        dispatch(postSendPhoneVerificationCode(email))
      }
      dispatch(createSetIsExtraVerificationPopupOpen(true))
    }
  }, [email, showVerifyPhoneForm, userEmailVerified, showSSOLogin])

  const remoteConfigLoaded = useSelector(selectLoadedFromRemoteConfigState)

  // Show loading spinner while remote config is loading to prevent form flash
  // between old and new sign-in forms when SSO feature flag is enabled
  if (!remoteConfigLoaded) {
    return (
      <UnauthedPageContainer>
        <LoadingBackgroundPage />
      </UnauthedPageContainer>
    )
  }

  const signInContent =
    env === Environments.Prod ? (
      <GoogleReCaptchaProvider
        reCaptchaKey={'6Lda69MgAAAAALa0nw9lUWo7NBrvsQzwwAvwEAuL'}
      >
        <SigninForm
          className={'signinForm'}
          email={email}
          setEmail={dispatchSetEmail}
          password={password}
          setPassword={setPassword}
          postSignin={dispatchSignin}
          isSigningIn={isLookingUp || isSigningIn}
          errorDetailMap={lookupErrorDetailMap || signinErrorDetailMap}
          topLevelError={signinFormTopLevelError()}
          didSessionExpireAutomatically={didSessionExpireAutomatically}
        />
      </GoogleReCaptchaProvider>
    ) : (
      <SigninForm
        className={'signinForm'}
        email={email}
        setEmail={dispatchSetEmail}
        password={password}
        setPassword={setPassword}
        postSignin={dispatchSignin}
        isSigningIn={isLookingUp || isSigningIn}
        errorDetailMap={lookupErrorDetailMap || signinErrorDetailMap}
        topLevelError={signinFormTopLevelError()}
        didSessionExpireAutomatically={didSessionExpireAutomatically}
      />
    )

  // determine content to show
  let content
  if (isCspAuthenticated) {
    content = <Rdrct to={cspSigninUrl()} />
  } else if (showVerifyForm) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        <ExtraVerificationPopup
          email={email}
          password={password}
          step={1}
          defaultWrongEmailText={dispatchSetUserStatus}
          isVerifyingUser={isVerifyingUser || isSigningIn}
          verifyTopLevelError={verifyEmailTopLevelError}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
          verificationResent={verificationEmailResent}
        />
      </PageAndBoxContainer>
    )
  } else if (
    isExtraVerificationPopupOpen &&
    (showVerifyPhoneForm || userEmailVerified)
  ) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        {requirePhoneNumber ? (
          renderRequirePhoneVerification()
        ) : (
          <ExtraVerificationPopup
            email={email}
            password={password}
            step={2}
            isVerifyingUser={verifyPhoneLoading}
            verifyTopLevelError={verifyPhoneTopLevelError}
            verificationResent={verificationPhoneResent ?? false}
            resendVerificationTopLevelError={resendVerificationTopLevelError}
          />
        )}
      </PageAndBoxContainer>
    )
  } else if (mfaChallengeInitiated) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        <VerifyForm
          id={'verifyCode'}
          data-testid="mfaVerifyForm"
          email={email}
          password={password}
          title={<Trns source={'Verify Code'} />}
          subtitle={
            <>
              <p>
                <Trns
                  source={
                    'Enter the 6-digit verification code sent to ${yourEmailOrPhone}'
                  }
                  expressions={{
                    yourEmailOrPhone: (
                      <p
                        css={css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        {
                          mfaDeliveryMedium === MfaDeliveryMedium.Email
                            ? mfaDestination
                            : '(•••) •••-' + mfaDestination?.slice(-4)
                          // ^ must manually format phone number since its not valid (format given is '+*******1234')
                        }
                      </p>
                    ),
                  }}
                />
              </p>
              <Trns
                source={
                  'Prefer ${otherMethod}? Update your code delivery method in Profile Settings.'
                }
                expressions={{
                  otherMethod: (
                    <Trns
                      source={
                        mfaDeliveryMedium === MfaDeliveryMedium.Email
                          ? MfaDeliveryMedium.SMS
                          : 'email'
                      }
                    />
                  ),
                }}
              />
            </>
          }
          buttonText={<Trns source={'Verify Code'} />}
          isVerifyingUser={isVerifyingUser || isSigningIn}
          verifyTopLevelError={
            verifyEmailTopLevelError ||
            signinTopLevelError ||
            CSPVerifyEmailTopLevelError
          }
          resendVerificationTopLevelError={resendVerificationTopLevelError}
          verificationResent={verificationEmailResent}
          setStatus={dispatchAbandonMfaChallenge}
          confirmCognitoSignin={dispatchConfirmCognitoSignin}
          postCognitoResendMfaCode={dispatchSignin}
          allowRememberMyDevice
        />
      </PageAndBoxContainer>
    )
  } else if (
    (userStatus !== UserStatus.NotVerified &&
      signinTopLevelError !== UserStatus.NotVerified) ||
    isSigningIn ||
    isLookingUp
  ) {
    if (brand === SupportedBrands.Extend) {
      content = (
        <div css={style}>
          {/* UnauthedPageContainer and UnauthedBoxContainer used instead of PageAndBoxContainer
           * here of PageAndBoxContainer here because different styling is needed
           */}
          <UnauthedPageContainer
            css={halfPageStyling}
            topLeftImage={
              showVerifyForm && brandName !== SupportedBrands.Bmo ? (
                <div
                  css={css`
                    position: relative;
                    top: 30px;
                    left: 30px;
                  `}
                >
                  <BrandPrimaryLogo />
                </div>
              ) : undefined
            }
          >
            <UnauthedBoxContainer
              className={'unauthedBoxContainer'}
              hideBrandLogo={showVerifyForm}
              hideFooter={showVerifyForm || showVerifyPhoneForm}
              noBox={showVerifyForm || showVerifyPhoneForm}
              boxStyling={boxStyling}
            >
              {signInContent}
            </UnauthedBoxContainer>
          </UnauthedPageContainer>
          <SigninCMS />
        </div>
      )
    } else {
      content = (
        <PageAndBoxContainer
          showVerifyForm={showVerifyForm}
          brandName={brandName}
          showVerifyPhoneForm={showVerifyPhoneForm}
        >
          {signInContent}
        </PageAndBoxContainer>
      )
    }
  } else if (!mfaChallengeInitiated && !showVerifyForm) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        <VerifyForm
          id={'verifyEmail'}
          email={email}
          password={password}
          title={<Trns source={'Verify Email'} />}
          subtitle={
            <p>
              <Trns
                source={'Enter the verification code sent to ${destination}'}
                expressions={{
                  destination: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {email}
                    </span>
                  ),
                }}
              />
            </p>
          }
          buttonText={<Trns source={'Verify Email'} />}
          isVerifyingUser={isVerifyingUser || isSigningIn}
          verifyTopLevelError={verifyEmailTopLevelError}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
          verificationResent={verificationEmailResent}
          setStatus={dispatchSetUserStatus}
          postVerification={dispatchPostUserVerificationThenSignin}
          postResendVerification={dispatchPostUserResendVerification}
          acceptOrgInvite={acceptOrgInvite}
        />
      </PageAndBoxContainer>
    )
  }
  // Helper function to render appropriate form content for SSO flow
  const renderSSOFormContent = (): React.ReactElement => {
    if (isSigningIn || isVerifyingUser) {
      return <Processing />
    }

    // Step 2: Phone verification (check this first!)
    // For this step, isExtraVerificationPopup will be true, while the email
    // verification step check will also be true. This check happens first because
    // the step 1 will return true for both steps
    if (
      isExtraVerificationPopupOpen &&
      (showVerifyPhoneForm || userEmailVerified)
    ) {
      if (requirePhoneNumber) {
        return renderRequirePhoneVerification()
      }
      return (
        <ExtraVerificationPopup
          email={email}
          password={password}
          step={2}
          isVerifyingUser={verifyPhoneLoading}
          verifyTopLevelError={verifyPhoneTopLevelError}
          verificationResent={verificationPhoneResent ?? false}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
        />
      )
    }

    // Step 1: Email verification
    if (showVerifyForm && isExtraVerificationPopupOpen === false) {
      return (
        <ExtraVerificationPopup
          email={email}
          password={password}
          step={1}
          defaultWrongEmailText={dispatchSetUserStatus}
          isVerifyingUser={isVerifyingUser || isSigningIn}
          verifyTopLevelError={verifyEmailTopLevelError}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
          verificationResent={verificationEmailResent}
        />
      )
    }

    // Default: Sign in form
    return <SignInFormV2 onPasswordChange={setPassword} />
  }

  // NOTE: Logic bifurcation for SSO Login is here.
  // This current page is too dependent on redux and
  // this waterfall conditional design of this page is
  // very difficult to maintain.
  // Let's try to move to something else now.
  if (showSSOLogin) {
    // Determine what content to show for SSO login flow
    let ssoContent
    if (mfaChallengeInitiated) {
      // Show MFA verification form - reuse the same content from non-SSO flow
      ssoContent = content
    } else if (brand === SupportedBrands.Extend) {
      ssoContent = (
        <div css={style}>
          {/* UnauthedPageContainer and UnauthedBoxContainer used instead of PageAndBoxContainer
           * because different styling is needed
           */}
          <UnauthedPageContainer css={halfPageStyling}>
            <UnauthedBoxContainer
              className={'unauthedBoxContainer'}
              hideBrandLogo={showVerifyForm}
              hideFooter={showVerifyForm || showVerifyPhoneForm}
              noBox={showVerifyForm || showVerifyPhoneForm}
              boxStyling={boxStyling}
            >
              {renderSSOFormContent()}
            </UnauthedBoxContainer>
          </UnauthedPageContainer>
          <SigninCMS />
        </div>
      )
    } else {
      ssoContent = (
        <PageAndBoxContainer
          showVerifyForm={showVerifyForm}
          brandName={brandName}
          showVerifyPhoneForm={showVerifyPhoneForm}
        >
          {renderSSOFormContent()}
        </PageAndBoxContainer>
      )
    }

    return (
      <>
        <Helmet>
          <title>{`Create virtual cards with the card in your pocket. | ${appName}`}</title>
          <meta
            name="description"
            content={`Create, send, and manage virtual cards for all your business payments and simplify expense processes with ${appName}.`}
          />
        </Helmet>
        {ssoContent}
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Create virtual cards with the card in your pocket. | ${appName}`}</title>
        <meta
          name="description"
          content={`Create, send, and manage virtual cards for all your business payments and simplify expense processes with ${appName}.`}
        />
      </Helmet>
      {content}
    </>
  )
}

export default SigninPage
